import {
  MINIMUM_ORDER_ERROR,
  minimumOrderError,
  quantityOnHandError,
} from "../../products/listing-counts";
import { EMAIL_FORMAT } from "../../formats/email";
import { hasFileId } from "../../products/file-spec";
import { isFieldShown } from "../../products/show-when";
import { isUnknownRecord, toUnknownRecord } from "../narrow";
import { validateTruckCapacities } from "./truck-capacities";
import type {
  Flow,
  ValidationContext,
  ValidationError,
  FieldDefinition,
} from "./types";
import {
  findMissingListingDimensions,
  findUndersizedListingDimensions,
  REQUIRED_LISTING_DIMENSIONS,
} from "../../products/listing-dimensions";

function extractValue(data: Record<string, unknown>, path: string): unknown {
  const parts = path.split(".");
  let current: unknown = data;
  for (const part of parts) {
    if (!isUnknownRecord(current)) return undefined;
    current = current[part];
  }
  return current;
}

function flowOf(context: ValidationContext): Flow {
  return context.intent === "recycle" ? "recycle" : "sell";
}

function appliesToFlow(
  field: FieldDefinition,
  context: ValidationContext,
): boolean {
  if (!field.showInFlows) return true;
  return field.showInFlows.includes(flowOf(context));
}

function isBlankAnswer(field: FieldDefinition, value: unknown): boolean {
  if (value === undefined || value === null) return true;
  if (typeof value === "string") return value.trim() === "";
  return field.type === "file" ? !hasFileId(value) : false;
}

export function isSpecFieldRequiredInFlow(
  field: Pick<FieldDefinition, "required" | "requiredInFlows">,
  flow: Flow,
): boolean {
  return (
    Boolean(field.required) || Boolean(field.requiredInFlows?.includes(flow))
  );
}

function isRequiredHere(
  field: FieldDefinition,
  context: ValidationContext,
): boolean {
  return isSpecFieldRequiredInFlow(field, flowOf(context));
}

function isFieldVisible(
  field: FieldDefinition,
  specs: Record<string, unknown>,
  context: ValidationContext,
): boolean {
  return appliesToFlow(field, context) && isFieldShown(field.showWhen, specs);
}


const DIMENSION_NAMES = new Set(["length", "width", "height", "capacity"]);

export function validateIntent(value: unknown): string | null {
  if (!value) return "Please select whether you want to sell or recycle";
  return null;
}

export function validateProductType(value: unknown): string | null {
  if (!value || (typeof value === "string" && !value.trim()))
    return "Please select a product type";
  return null;
}

export function validatePhotos(value: unknown): string | null {
  if (!Array.isArray(value) || value.length === 0)
    return "Please upload at least one photo";
  return null;
}

export function validateCondition(
  value: unknown,
  context: ValidationContext,
): string | null {
  if (context.conditionsForType.length === 0) return null;
  if (!value) return "Please select a condition";
  return null;
}

export function validatePrice(value: unknown): string | null {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return "Please enter a price";
  }
  if (value < 0) {
    return "Price cannot be negative";
  }
  return null;
}

export function validateQuantity(value: unknown): string | null {
  if (typeof value !== "number" || value <= 0)
    return "Please enter quantity available";
  return quantityOnHandError(value);
}

export function validateMinOrderQuantity(
  value: unknown,
  context: ValidationContext,
): string | null {
  if (typeof value !== "number") return MINIMUM_ORDER_ERROR;
  return minimumOrderError(value, context.quantityAvailable);
}

export function validateWeight(value: unknown): string | null {
  if (value === undefined || value === null) return null;
  if (typeof value === "number" && value <= 0) return "Weight must be positive";
  return null;
}

export function validateLeadTime(value: unknown): string | null {
  if (value === undefined || value === null) return null;
  if (typeof value !== "number" || !Number.isFinite(value))
    return "Lead time must be a valid number";
  if (value < 0) return "Lead time cannot be negative";
  return null;
}

export function validateContactFirstName(value: unknown): string | null {
  if (!value || (typeof value === "string" && !value.trim()))
    return "First name is required";
  return null;
}

export function validateContactLastName(value: unknown): string | null {
  if (!value || (typeof value === "string" && !value.trim()))
    return "Last name is required";
  return null;
}

export function validateContactEmail(value: unknown): string | null {
  if (!value || (typeof value === "string" && !value.trim()))
    return "Email is required";
  if (typeof value === "string" && !EMAIL_FORMAT.validate(value))
    return EMAIL_FORMAT.errorMessage;
  return null;
}

export function validateLocation(
  data: Record<string, unknown>,
): ValidationError[] {
  if (!data.cityName || !data.state) {
    return [{ field: "location", message: "Please select a pickup location" }];
  }
  if (!data.addressId && !data.addressLine1) {
    return [{ field: "location", message: "Street address is required" }];
  }
  return [];
}

export function validateDimensions(
  data: Record<string, unknown>,
  context: ValidationContext,
): ValidationError[] {
  if (!context.productTypeSlug) return [];

  const specs = toUnknownRecord(data.specs);
  const dimensions = toUnknownRecord(data.dimensions);
  const labels = new Map(
    REQUIRED_LISTING_DIMENSIONS.map(({ name, label }) => [name, label]),
  );
  const errors: ValidationError[] = [
    ...findMissingListingDimensions(dimensions).map((field) => ({
      field,
      message: `${labels.get(field)} is required`,
    })),
    ...findUndersizedListingDimensions(context.productTypeSlug, dimensions).map(
      ({ name, message }) => ({ field: name, message }),
    ),
  ];
  const dimensionFields = context.fields.filter(
    (field) => DIMENSION_NAMES.has(field.name) && isFieldVisible(field, specs, context),
  );

  const requiredFields = dimensionFields.filter(
    (field) =>
      isRequiredHere(field, context) &&
      !REQUIRED_LISTING_DIMENSIONS.some(({ name }) => name === field.name),
  );

  for (const field of requiredFields) {
    const value = specs[field.name] ?? dimensions[field.name];
    if (
      value === undefined ||
      value === null ||
      value === "" ||
      (typeof value === "number" && value <= 0)
    ) {
      errors.push({
        field: field.name,
        message: `${field.label} is required`,
      });
    }
  }

  return errors;
}

export function validateSpecs(
  data: Record<string, unknown>,
  context: ValidationContext,
): ValidationError[] {
  if (!context.productTypeSlug) return [];

  const specs = toUnknownRecord(data.specs);

  const specFields = context.fields.filter(
    (field) =>
      !DIMENSION_NAMES.has(field.name) &&
      field.name !== "condition" &&
      isFieldVisible(field, specs, context),
  );

  const requiredFields = specFields.filter((field) => isRequiredHere(field, context));
  const errors: ValidationError[] = [];

  for (const field of requiredFields) {
    if (isBlankAnswer(field, specs[field.name])) {
      errors.push({
        field: field.name,
        message: `${field.label} is required`,
      });
    }
  }

  return errors;
}


export { validateTruckCapacities } from "./truck-capacities";

interface ValidatorEntry {
  path?: string;
  validate?: (value: unknown, context: ValidationContext) => string | null;
  validateMulti?: (
    data: Record<string, unknown>,
    context: ValidationContext,
  ) => ValidationError[];
}

const VALIDATOR_REGISTRY: Record<string, ValidatorEntry> = {
  intent: { path: "intent", validate: validateIntent },
  productTypeSlug: {
    path: "productTypeSlug",
    validate: validateProductType,
  },
  photos: { path: "photos", validate: validatePhotos },
  condition: { path: "condition", validate: validateCondition },
  dimensions: { validateMulti: validateDimensions },
  specs: { validateMulti: validateSpecs },
  priceCents: { path: "priceCents", validate: validatePrice },
  quantityAvailable: {
    path: "quantityAvailable",
    validate: validateQuantity,
  },
  minOrderQuantity: {
    path: "minOrderQuantity",
    validate: validateMinOrderQuantity,
  },
  location: { validateMulti: validateLocation },
  truckCapacities: { validateMulti: validateTruckCapacities },
  weightPerUnitLbs: { path: "weightPerUnitLbs", validate: validateWeight },
  leadTimeDays: { path: "leadTimeDays", validate: validateLeadTime },
  "contact.firstName": {
    path: "contact.firstName",
    validate: validateContactFirstName,
  },
  "contact.lastName": {
    path: "contact.lastName",
    validate: validateContactLastName,
  },
  "contact.email": {
    path: "contact.email",
    validate: validateContactEmail,
  },
};

export function validateFields(
  fieldNames: string[],
  data: Record<string, unknown>,
  context: ValidationContext,
): ValidationError[] {
  const errors: ValidationError[] = [];

  for (const fieldName of fieldNames) {
    const entry = VALIDATOR_REGISTRY[fieldName];
    if (!entry) continue;

    if (entry.validateMulti) {
      errors.push(...entry.validateMulti(data, context));
    } else if (entry.validate && entry.path) {
      const value = extractValue(data, entry.path);
      const error = entry.validate(value, context);
      if (error) {
        errors.push({ field: fieldName, message: error });
      }
    }
  }

  return errors;
}

export function validateAll(
  data: Record<string, unknown>,
  context: ValidationContext,
): ValidationError[] {
  const fieldNames = [
    "intent",
    "productTypeSlug",
    "photos",
    "condition",
    "dimensions",
    "specs",
    "location",
    "quantityAvailable",
    "priceCents",
    "minOrderQuantity",
    "truckCapacities",
    "weightPerUnitLbs",
    "leadTimeDays",
  ];

  if (data.contact !== undefined) {
    fieldNames.push("contact.firstName", "contact.lastName", "contact.email");
  }

  return validateFields(fieldNames, data, context);
}
